import { NavLink, Outlet } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAuth } from "../auth/index.js";
import { useAppStore } from "../store/useAppStore.js";
import { DataProvider } from "../store/DataProvider.jsx";
import { useTheme } from "../utils/useTheme.js";

const NAV_ITEMS = [
   { to: "/dashboard", label: "Dashboard", icon: "🧭" },
   { to: "/board", label: "Board", icon: "📋" },
   { to: "/practice", label: "Practice", icon: "🎙️" },
   { to: "/salary", label: "Salary", icon: "$" },
   { to: "/settings", label: "Settings", icon: "⛭" },
   { to: "/help", label: "Help", icon: "?" },
   // { to: "/import", label: "Import", icon: "↑" },
];

function NavBadge({ count }) {
   if (!count) return null;
   return (
      <span className="ml-auto rounded-full bg-accent-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">
         {count}
      </span>
   );
}

export function Layout() {
   const { signOut } = useAuth();
   const { theme, toggleTheme } = useTheme();
   const applications = useAppStore((s) => s.applications);
   const [moreOpen, setMoreOpen] = useState(false);

   const reminderCount = useMemo(() => {
      const today = new Date().toISOString().slice(0, 10);
      return applications.filter(
         (a) => a.nextActionDate && a.nextActionDate <= today && a.status !== "rejected",
      ).length;
   }, [applications]);

   return (
      <div className="flex h-full">
         <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-primary-600 focus:text-white focus:px-4 focus:py-2 focus:rounded"
         >
            Skip to content
         </a>
         <aside className="hidden sm:flex w-56 flex-col border-r border-slate-200 bg-slate-50">
            <div className="px-4 py-5">
               <h1 className="text-lg font-bold text-primary-700">Launchpad</h1>
            </div>

            <nav className="flex-1 space-y-1 px-2">
               {NAV_ITEMS.map(({ to, label, icon }) => (
                  <NavLink
                     key={to}
                     to={to}
                     className={({ isActive }) =>
                        `flex items-center gap-2 rounded px-3 py-2 text-sm font-medium transition-colors ${
                           isActive
                              ? "bg-primary-100 text-primary-700"
                              : "text-slate-700 hover:bg-slate-100"
                        }`
                     }
                  >
                     <span>{icon}</span>
                     {label}
                     {to === "/dashboard" && <NavBadge count={reminderCount} />}
                  </NavLink>
               ))}
            </nav>

            <div className="border-t border-slate-200 p-2 space-y-1">
               <button
                  onClick={toggleTheme}
                  className="w-full rounded px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-100"
               >
                  {theme === "dark" ? "☀ Light Mode" : "☾ Dark Mode"}
               </button>
               <button
                  onClick={signOut}
                  className="w-full rounded px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-100"
               >
                  Sign Out
               </button>
            </div>
         </aside>

         <main id="main-content" className="flex-1 overflow-auto bg-white pb-16 sm:pb-0">
            <DataProvider>
               <Outlet />
            </DataProvider>
         </main>

         {/* Mobile bottom nav */}
         <nav className="sm:hidden fixed bottom-0 inset-x-0 flex border-t border-slate-200 bg-white z-40 pb-[env(safe-area-inset-bottom)]">
            {NAV_ITEMS.slice(0, 4).map(({ to, label, icon }) => (
               <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                     `flex-1 flex flex-col items-center py-2 text-[10px] ${isActive ? "text-primary-700 font-medium" : "text-slate-500"}`
                  }
               >
                  <span className="text-base">{icon}</span>
                  {label}
               </NavLink>
            ))}
            <button
               onClick={() => setMoreOpen((o) => !o)}
               className={`flex-1 flex flex-col items-center py-2 text-[10px] ${moreOpen ? "text-primary-700 font-medium" : "text-slate-500"}`}
            >
               <span className="text-base">⋯</span>
               More
            </button>
         </nav>

         {/* More menu popover */}
         {moreOpen && (
            <div className="sm:hidden fixed bottom-14 right-2 z-50 rounded-lg border border-slate-200 bg-white shadow-lg py-1 w-44">
               {NAV_ITEMS.slice(4).map(({ to, label, icon }) => (
                  <NavLink
                     key={to}
                     to={to}
                     onClick={() => setMoreOpen(false)}
                     className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                     <span>{icon}</span>
                     {label}
                  </NavLink>
               ))}
               <button
                  onClick={() => {
                     toggleTheme();
                     setMoreOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
               >
                  <span>{theme === "dark" ? "☀" : "☾"}</span>
                  {theme === "dark" ? "Light Mode" : "Dark Mode"}
               </button>
               <button
                  onClick={signOut}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
               >
                  <span>↪</span>
                  Sign Out
               </button>
            </div>
         )}
      </div>
   );
}
